import { getGroupsContainingAsset } from '#base/AssetGroup.js';
import { Actions } from '#base/Canvas/Actions.js';
import { DesignerDispatch } from '#base/Canvas/Dispatch.js';
import { getAverageRgb, getGrayScale, getRgb } from '#base/Color.js';
import { DesignOption } from '#base/Designer/index.js';
import GroupOrderCTA from '#base/Designer/GroupOrderCTA.js';
import { CustomImageSelect, CustomImageUpload, ImageSelect } from '#base/Image.js';
import ArtworkToolIcon from '#base/resources/icons/artwork-tool.svg';
import GroupJerseyToolIcon from '#base/resources/icons/group/jersey-tool.svg';
import GroupMonogramToolIcon from '#base/resources/icons/group/monogram-tool.svg';
import GroupTextToolIcon from '#base/resources/icons/group/text-tool.svg';
import GroupTitleToolIcon from '#base/resources/icons/group/title-tool.svg';
import JerseyToolIcon from '#base/resources/icons/jersey-tool.svg';
import MonogramToolIcon from '#base/resources/icons/monogram-tool.svg';
import TextToolIcon from '#base/resources/icons/text-tool.svg';
import UserArtToolIcon from '#base/resources/icons/user-art-tool.svg';
import {
  FontGroupSelect,
  FontSelect,
  JerseyGroupSelect,
  JerseySelect,
  MonogramGroupSelect,
  MonogramSelect,
  TitleGroupSelect,
} from '#base/Text/index.js';
import Button from '#base/ui/Button.js';
import { DEFAULT_COLOR, DEFAULT_COLOR_DARK, DEFAULT_COLOR_LIGHT } from '@c2s-npm/plumlib';
import classNames from 'classnames';
import React, {
  forwardRef,
  memo,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useMediaQuery } from 'react-responsive';
import { Maybe } from '#base/types.js';
import FeaturedGroup from './FeaturedGroup.js';
import ElementAttributes from './ElementAttributes.js';

export interface IForwardedRefs {
  undo: any;
}

interface IProps {
  assetLibrary: any;
  assetGroups: any;
  assetGroupFilter: any;
  config: any;
  uploaded: any;
  uploadUrl: any;
  onUpload: any;
  allFacesElements: any;
  elements: any;
  selectedFace: any;
  selectedElements: any;
  view: string;
  setView: (view: string) => void;
  withText: boolean;
  withGroup: boolean;
  withJersey: boolean;
  withMonogram: boolean;
  withArtwork: boolean;
  withUpload: boolean;
  wantUploadsDeferred: boolean;
  hasPremium: string;
  hasElements: boolean;
  restrictionInfo: object;
  graphicType: string;
  uploadFee: { rate: string; usualRate: string } | null;
  typeId: number;
  debug: boolean;
  mode?: string;
  units?: string;
}

const DesignTools: React.ForwardRefRenderFunction<IForwardedRefs, IProps> = (
  {
    assetLibrary,
    assetGroups,
    config,
    uploaded,
    uploadUrl,
    onUpload,
    allFacesElements,
    elements,
    selectedFace,
    selectedElements,
    view,
    setView,
    withText,
    withGroup,
    withJersey,
    withMonogram,
    withArtwork,
    withUpload,
    wantUploadsDeferred,
    assetGroupFilter,
    hasPremium,
    // hasElements,
    restrictionInfo,
    graphicType,
    uploadFee,
    typeId,
    debug,
    mode,
    units,
  },
  forwardedRef
) => {
  const dispatch = useContext(DesignerDispatch);
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ maxWidth: 1023 });
  const [defaultSelected, setDefaultSelected] = useState(null);

  const updateElement = (data) => {
    // TODO find a better way to do this? (too much scrolling for my taste) maybe just do it on certain actions (lots of things update elements)
    isMobile &&
      ['artwork', 'file', 'uploaded'].includes(data?.elementType) &&
      document.getElementById('miyo')?.scrollIntoView();
    data && dispatch(Actions.updateSelectedElement(selectedFace, data));
  };

  const removeElement = (data) => {
    data && dispatch(Actions.removeElement(selectedFace, data.get ? data.get('id') : data.id || data));
  };

  const selectedElement =
    selectedElements &&
    selectedElements.length === 1 &&
    elements.find((e) => e.get('id') === selectedElements[0]);

  const [wasViewClicked, setWasViewClicked] = useState(false);

  const defaultColor = useMemo(() => {
    if (graphicType === 'engraved') return null;

    const colors = (config?.[0]?.item?.color || []).filter((c) => !!c);
    if (colors.length > 0) {
      const rgb = colors.map(getRgb);
      const avg = getAverageRgb(rgb);
      const grey = getGrayScale(avg);
      const color = grey >= 128 ? DEFAULT_COLOR_DARK : DEFAULT_COLOR_LIGHT;
      return color;
    } else {
      return DEFAULT_COLOR;
    }
  }, [config, graphicType]);

  useEffect(() => {
    if (selectedElement) {
      const isGroup = selectedElement.get('isGroup');
      const type = selectedElement.get('elementType');
      if (isGroup) {
        setView('group.' + type);
      } else {
        if (view !== type) {
          const isFixed = selectedElement.get('fixed');
          setView(isFixed ? 'premium' : type);
        }
      }
      setWasViewClicked(false);
    } else if (!wasViewClicked) {
      setView(null);
    }
  }, [selectedElement, wasViewClicked]);

  const onClick = (
    viewname,
    options: { isGroup?: boolean; defaultSelected?: Maybe<number> } = {
      isGroup: false,
      defaultSelected: null,
    }
  ) => {
    if (options?.isGroup) {
      const group = allFacesElements.find((element) => element?.isGroup || false);

      // const selectedFace = group?.faces?.find((face) => face?.id === selectedFace);

      if (!group?.id) {
        dispatch(Actions.selectNone());
      } else {
        dispatch(Actions.selectElement(group.face, group.id));
      }
    } else {
      // Since we are clicking on a tool, we are creating a new object.
      // Therefore, nothing should be selected, which will cause the
      // component to create a new object.
      dispatch(Actions.selectNone());
    }
    setView(viewname);
    setDefaultSelected(options.defaultSelected || null);
    setWasViewClicked(true);
  };

  const showView = view && (selectedElement || wasViewClicked);

  const goHome = () => {
    onClick(null);
  };
  const goHomeGroup = () => {
    // onClick(null, { isGroup: true });
    goHome();
  };

  const initial = useMemo(() => selectedElement, [view]);
  const revert = () => {
    if (initial) {
      updateElement(initial.toJS ? initial.toJS() : initial);
    } else {
      removeElement(selectedElement);
    }
  };
  const undo = () => {
    revert();
    if (!initial && !!view.match(/^group./)) {
      onClick('group');
    } else {
      goHome();
    }
  };

  useImperativeHandle(
    forwardedRef,
    () => ({
      undo,
    }),
    [undo]
  );

  const hasPremiumFace = hasPremium === 'face';

  const premiumGroups = useMemo(() => {
    if (hasPremiumFace) {
      const assetId = elements.getIn([0, 'assetId']);
      const groups = getGroupsContainingAsset(assetGroups.artwork, assetId);
      return groups;
    } else {
      return [];
    }
  }, [hasPremiumFace, assetLibrary, assetGroups, elements]);
  const hasPremiumGroups = premiumGroups?.length > 0;

  const selectPremium = () => {
    const id = elements.getIn([0, 'id']);
    id && dispatch(Actions.selectElement(selectedFace, id));
  };

  const nonExcludedArtworkGroups = useMemo(() => {
    if (!withArtwork || !typeId || !assetGroups?.artwork) return [];

    return (
      assetGroups.artwork.filter((g) => (g.attributes?.excludedItemTypeIds || []).indexOf(typeId) === -1) ||
      []
    );
  }, [assetGroups, typeId, withArtwork]);

  const featuredArtworkGroups = useMemo(() => {
    return (
      nonExcludedArtworkGroups
        .filter((g) => g.featured)
        .map((g) => ({
          ...g,
          onClick: () => onClick('artwork', g.assets.length > 0 ? { defaultSelected: g.assets[0] } : null),
        })) || []
    );
  }, [nonExcludedArtworkGroups]);

  return (
    <>
      {!showView && (
        <>
          <div className='t-mx-auto t-flex t-justify-start t-gap-2 t-overflow-x-auto lg:t-grid lg:t-max-w-[360px] lg:t-grid-cols-3 lg:t-flex-col lg:t-gap-4 lg:t-overflow-x-hidden lg:t-py-3'>
            {!hasPremiumGroups && withText && (
              <DesignOption
                iconUrl={TextToolIcon}
                text='Text'
                onClick={() => onClick('font')}
                disabled={hasPremiumFace}
                mode={isTablet ? 'vertical' : 'grid'}
                className='lg:t-mx-auto'
              />
            )}
            {!hasPremiumGroups && withMonogram && (
              <DesignOption
                iconUrl={MonogramToolIcon}
                text='Monogram'
                onClick={() => onClick('monogram')}
                disabled={hasPremiumFace}
                mode={isTablet ? 'vertical' : 'grid'}
                className='lg:t-mx-auto'
              />
            )}
            {!hasPremiumGroups && withJersey && (
              <DesignOption
                iconUrl={JerseyToolIcon}
                text='Jersey'
                onClick={() => onClick('jersey')}
                disabled={hasPremiumFace}
                mode={isTablet ? 'vertical' : 'grid'}
                className='lg:t-mx-auto'
              />
            )}
            {!hasPremiumGroups && withArtwork && (
              <DesignOption
                iconUrl={ArtworkToolIcon}
                text='Artwork'
                onClick={() => onClick('artwork')}
                disabled={hasPremiumFace}
                mode={isTablet ? 'vertical' : 'grid'}
                className='lg:t-mx-auto'
              />
            )}
            {hasPremiumGroups && (
              <DesignOption
                iconUrl={ArtworkToolIcon}
                text='Design Gallery'
                onClick={selectPremium}
                disabled={false}
                mode={isTablet ? 'vertical' : 'grid'}
                className='lg:t-mx-auto'
              />
            )}

            {withUpload && (
              <DesignOption
                iconUrl={UserArtToolIcon}
                text={isTablet ? 'Upload' : 'Upload Design'}
                subtext={
                  uploadFee?.rate && (
                    <span
                      className={classNames({
                        't-text-brand-black': !uploadFee?.usualRate,
                        't-text-onsale t-font-bold': !!uploadFee?.usualRate,
                      })}>
                      {uploadFee?.rate}{' '}
                      {uploadFee?.usualRate && (
                        <span className='t-text-brand-black t-font-normal t-line-through'>
                          {uploadFee.usualRate}
                        </span>
                      )}
                    </span>
                  )
                }
                onClick={() => onClick('upload')}
                disabled={!!hasPremium}
                mode={isTablet ? 'vertical' : 'grid'}
                className='lg:t-mx-auto'
              />
            )}
          </div>

          {withArtwork && featuredArtworkGroups.length > 0 && (
            <div className='t-flex t-flex-col t-gap-5 t-my-5'>
              {featuredArtworkGroups.map((group) => (
                <FeaturedGroup key={group.id} onClick={group.onClick} group={group} />
              ))}
            </div>
          )}

          {withGroup && (
            <GroupOrderCTA
              onClick={() => onClick('group', { isGroup: true })}
              elementsCount={allFacesElements.find((element) => element?.isGroup)?.data?.length || 0}
            />
          )}
        </>
      )}
      {showView && (
        <>
          {view === 'group' && (
            <>
              <div className='-t-mx-4 -t-mt-5 lg:-t-mx-10 lg:-t-mt-8'>
                <p className='t-mb-0 t-border t-border-neutrals-light-grey t-p-[10px] t-text-xs t-font-bold md:t-hidden'>
                  Name Template
                </p>
                <p className='t-mx-auto t-mb-0 t-mt-4 t-max-w-[360px] t-px-4 t-text-xs t-font-normal'>
                  Select from one of the following templates. Make each item unique with its own name, title,
                  or number.
                </p>
              </div>

              <div className='t-mx-auto t-grid t-max-w-[225px] t-grid-cols-2 t-justify-start t-gap-2 t-overflow-x-auto t-py-3 2xs:t-max-w-[360px] 2xs:t-grid-cols-3 lg:t-gap-4'>
                {withText && (
                  <DesignOption
                    iconUrl={GroupTextToolIcon}
                    text='Name'
                    onClick={() => onClick('group.font')}
                    mode='grid'
                    className='t-mx-auto'
                    disabled={hasPremiumFace}
                  />
                )}
                {withMonogram && (
                  <DesignOption
                    iconUrl={GroupMonogramToolIcon}
                    text='Monogram'
                    onClick={() => onClick('group.monogram')}
                    mode='grid'
                    className='t-mx-auto'
                    disabled={hasPremiumFace}
                  />
                )}
                {withJersey && (
                  <DesignOption
                    iconUrl={GroupJerseyToolIcon}
                    text='Jersey'
                    onClick={() => onClick('group.jersey')}
                    mode='grid'
                    className='t-mx-auto'
                    disabled={hasPremiumFace}
                  />
                )}
                {withJersey && (
                  <DesignOption
                    iconUrl={GroupTitleToolIcon}
                    text='Name &amp; Title'
                    onClick={() => onClick('group.title')}
                    mode='grid'
                    className='t-mx-auto'
                    disabled={hasPremiumFace}
                  />
                )}
              </div>

              <div className='t-mt-2 t-text-center'>
                <Button variant='link' size={isMobile ? 'sm' : 'base'} onClick={goHome}>
                  Cancel
                </Button>
              </div>
            </>
          )}
          {view === 'font' && (
            <FontSelect
              selected={selectedElement}
              onSelect={updateElement}
              fonts={assetLibrary.font}
              groups={assetGroups.font}
              cancel={goHome}
              revert={revert}
              {...{ restrictionInfo, hasPremium, elements, graphicType, defaultColor }}
            />
          )}
          {view === 'group.font' && (
            <FontGroupSelect
              selected={selectedElement}
              onSelect={updateElement}
              fonts={assetLibrary.font}
              groups={assetGroups.font}
              cancel={goHomeGroup}
              revert={revert}
              {...{ restrictionInfo, hasPremium, elements, graphicType, defaultColor }}
            />
          )}
          {view === 'monogram' && (
            <MonogramSelect
              selected={selectedElement}
              onSelect={updateElement}
              monograms={assetLibrary.monogram}
              groups={assetGroups.monogram}
              cancel={goHome}
              revert={revert}
              {...{ restrictionInfo, hasPremium, elements, graphicType, defaultColor }}
            />
          )}
          {view === 'group.monogram' && (
            <MonogramGroupSelect
              selected={selectedElement}
              onSelect={updateElement}
              monograms={assetLibrary.monogram}
              groups={assetGroups.monogram}
              cancel={goHomeGroup}
              revert={revert}
              {...{ restrictionInfo, hasPremium, elements, graphicType, defaultColor }}
            />
          )}
          {view === 'jersey' && (
            <JerseySelect
              selected={selectedElement}
              onSelect={updateElement}
              fonts={assetLibrary.jersey}
              groups={assetGroups.jersey}
              cancel={goHome}
              revert={revert}
              {...{ restrictionInfo, hasPremium, elements, graphicType, defaultColor }}
            />
          )}
          {view === 'group.jersey' && (
            <JerseyGroupSelect
              selected={selectedElement}
              onSelect={updateElement}
              fonts={assetLibrary.jersey}
              groups={assetGroups.jersey}
              cancel={goHomeGroup}
              revert={revert}
              {...{ restrictionInfo, hasPremium, elements, graphicType, defaultColor }}
            />
          )}
          {view === 'group.title' && (
            <TitleGroupSelect
              selected={selectedElement}
              onSelect={updateElement}
              fonts={assetLibrary.font}
              groups={assetGroups.font}
              cancel={goHomeGroup}
              revert={revert}
              {...{ restrictionInfo, hasPremium, elements, graphicType, defaultColor }}
            />
          )}

          {view === 'artwork' && (
            <ImageSelect
              selected={selectedElement}
              defaultSelectedElement={defaultSelected}
              onSelect={updateElement}
              artwork={assetLibrary.artwork}
              groups={nonExcludedArtworkGroups}
              cancel={goHome}
              revert={revert}
              {...{
                restrictionInfo,
                hasPremium,
                elements,
                assetGroupFilter,
                graphicType,
                defaultColor,
                defaultSelected,
              }}
            />
          )}

          {view === 'premium' && (
            <ImageSelect
              selected={selectedElement}
              onSelect={updateElement}
              artwork={assetLibrary.artwork}
              groups={assetGroups.artwork}
              cancel={goHome}
              revert={revert}
              autoSelectGroup={false}
              {...{ restrictionInfo, hasPremium, elements, assetGroupFilter, graphicType, defaultColor }}
            />
          )}

          {(view === 'uploaded' || view === 'file') && (
            <CustomImageSelect
              selected={selectedElement}
              onSelect={updateElement}
              {...{ uploadUrl, onUpload, uploaded }}
              cancel={goHome}
              revert={revert}
              {...{ restrictionInfo, hasPremium, elements, graphicType, defaultColor, mode }}
              uploadFee={uploadFee}
              config={config.find((c) => c.face === selectedFace)}
              graphicType={graphicType}
              defaultColor={defaultColor}
            />
          )}

          {view === 'upload' && (
            <CustomImageUpload
              onSelect={updateElement}
              config={config.find((c) => c.face === selectedFace)}
              wantUploadsDeferred={wantUploadsDeferred}
              {...{
                uploadUrl,
                onUpload,
                uploaded,
                onUploadedClick: () => onClick('uploaded'),
                uploadFee,
                restrictionInfo,
                hasPremium,
                elements,
                graphicType,
                defaultColor,
                cancel: goHome,
                mode,
              }}
            />
          )}

          {mode === 'pro' && selectedElement && (
            <ElementAttributes
              element={selectedElement}
              onChange={updateElement}
              debug={debug}
              units={units}
              config={config.find((c) => c.face === selectedFace)}
            />
          )}
        </>
      )}
    </>
  );
};

const ForwardedDesignTools = forwardRef(DesignTools);
export type DesignToolsRef = React.ElementRef<typeof ForwardedDesignTools>;

export default memo(ForwardedDesignTools);
